/**
 * Chunk C5 — Ask-for-credit request path ↔ CN document path bridge.
 * Chunk 2 — Open list + badge = credit asks only (editType credit_note_request).
 */

import { isCreditNoteDocument } from './creditNoteDocumentHelpers'
import { getLinkedInvoicesForReceiptingOrder } from './reconciliationHealth'
import {
  formatReconciliationAlertCutoff,
  isOnOrAfterReconciliationAlertCutoff,
} from './reconciliationAlertCutoff'

export const ASK_FOR_CREDIT_BRIDGE_COPY = {
  closeConfirmTitle: 'Close credit request(s)?',
  closeConfirmBody:
    'You applied a credit note to this PO. Mark all open Ask-for-credit lines on this PO as resolved so they leave your Credit Requests alert?',
  closeConfirmOk: 'Yes, mark resolved',
  closeConfirmCancel: 'Keep open',
  closeSuccess: 'Credit request(s) marked resolved',
  closeSkipped:
    'Credit request left open — you can resolve it anytime on Credit Requests.',

  teachingTitle: 'How credit requests work',
  teachingBody: [
    'On Goods receipting, tick Ask for credit? when something is short, damaged, or wrong — that sends a request to the supplier and keeps it on Credit Requests.',
    'When the supplier sends a credit note PDF, upload it as Credit Note in Invoices & Credit Notes and apply it to that PO. We’ll then ask if you want to close the request.',
    'The amber badge counts open Ask-for-credit requests only — not every goods-receipting problem. Lines where you didn’t ask for credit stay on Goods receipting (see Receipt issues if you need them here).',
    'If a request is still open: chase the supplier, upload the credit note if you have it, or mark resolved if you no longer need it (for example they replaced the goods). That clears the amber badge.',
  ],
  teachingHintApplied:
    'A credit note is already applied to this PO — mark resolved if the request is dealt with.',
  teachingHintChase:
    'Still waiting? Chase the supplier, or upload the credit note when it arrives.',
  teachingHintApproved:
    'Supplier approved — upload the Credit Note PDF (Invoices & Credit Notes) or mark resolved if you no longer need it.',
  receiptIssuesTeaching:
    'These are problem lines from goods receipting where Ask for credit was not ticked. They don’t count toward the amber badge — finish them on Goods receipting, or mark resolved here if you’re done.',

  emptyOpen: 'No open credit asks — nice and clear.',
  emptyResolved: 'No resolved credit asks yet.',
  emptyReceiptIssues: 'No open receipt issues without a credit ask.',

  badgeTooltip: count =>
    `${count} open credit ask${
      count === 1 ? '' : 's'
    } — chase, upload a Credit Note, or mark resolved when done`,
}

/**
 * Confirm copy after CN apply — always closes all open credit asks on that PO.
 */
export function buildCloseCreditRequestConfirmCopy({
  openCount = 1,
  purchaseOrderNumber = '',
} = {}) {
  const count = Math.max(1, Number(openCount) || 1)
  const po = String(purchaseOrderNumber || '').trim()
  const poBit = po ? ` on ${po}` : ' on this PO'

  if (count === 1) {
    return {
      title: ASK_FOR_CREDIT_BRIDGE_COPY.closeConfirmTitle,
      body: `You applied a credit note${poBit}. Mark the open Ask-for-credit line as resolved so it leaves your Credit Requests alert?`,
      okText: ASK_FOR_CREDIT_BRIDGE_COPY.closeConfirmOk,
      cancelText: ASK_FOR_CREDIT_BRIDGE_COPY.closeConfirmCancel,
    }
  }

  return {
    title: ASK_FOR_CREDIT_BRIDGE_COPY.closeConfirmTitle,
    body: `You applied a credit note${poBit}. Mark all ${count} open Ask-for-credit lines on this PO as resolved so they leave your Credit Requests alert?`,
    okText: ASK_FOR_CREDIT_BRIDGE_COPY.closeConfirmOk,
    cancelText: ASK_FOR_CREDIT_BRIDGE_COPY.closeConfirmCancel,
  }
}

export const CREDIT_REQUEST_VIEW = {
  OPEN: 'open',
  RESOLVED: 'resolved',
  RECEIPT_ISSUES: 'receipt-issues',
}

const OPEN_CREDIT_LINE_STATUSES = new Set([
  'Sent to supplier',
  'CN Requested',
  'Rejected by supplier',
])

function normalisePo(value) {
  return String(value || '').trim()
}

function receiptingDocsFromLine(line = {}) {
  const r = line?.receipting
  if (!r) return []
  if (Array.isArray(r)) return r.filter(Boolean)
  return [r]
}

export function getReceiptingFromHistoryRow(orderNote = {}) {
  return orderNote?._id?.receipting?.[0] || null
}

/** True Ask-for-credit / credit note request line. */
export function isCreditNoteRequestReceipting(receipting = null) {
  return String(receipting?.editType || '').trim() === 'credit_note_request'
}

/** Problem line without Ask for credit (does not drive the amber badge). */
export function isReceiptIssueNoCreditAsked(receipting = null) {
  return String(receipting?.editType || '').trim() === 'invoice_reconciliation'
}

export function isOpenHistoryRow(orderNote = {}) {
  const receipting = getReceiptingFromHistoryRow(orderNote)
  if (!receipting) return false
  return receipting.orderResolved !== true
}

export function isOpenCreditAskHistoryRow(orderNote = {}) {
  const receipting = getReceiptingFromHistoryRow(orderNote)
  return (
    isOpenHistoryRow(orderNote) && isCreditNoteRequestReceipting(receipting)
  )
}

export function isResolvedCreditAskHistoryRow(orderNote = {}) {
  const receipting = getReceiptingFromHistoryRow(orderNote)
  return (
    receipting?.orderResolved === true &&
    isCreditNoteRequestReceipting(receipting)
  )
}

export function isOpenReceiptIssueHistoryRow(orderNote = {}) {
  const receipting = getReceiptingFromHistoryRow(orderNote)
  return isOpenHistoryRow(orderNote) && isReceiptIssueNoCreditAsked(receipting)
}

/**
 * True when CN apply should offer “Close credit request?” (never auto-close).
 */
export function shouldPromptCloseCreditRequestAfterApply(order = null) {
  if (!order) return false
  const status = String(order.receiptingStatus || '').trim()
  if (
    status === 'CN Requested' ||
    status === 'Sent to supplier' ||
    status === 'Rejected by supplier'
  ) {
    return true
  }
  return collectOpenCreditReceiptingIdsFromOrder(order).length > 0
}

/**
 * Unresolved receipting ids on a GR order that look like open credit requests.
 */
export function collectOpenCreditReceiptingIdsFromOrder(order = {}) {
  const ids = []
  const lines = Array.isArray(order.products)
    ? order.products
    : Array.isArray(order.orders)
    ? order.orders
    : []

  for (const line of lines) {
    for (const doc of receiptingDocsFromLine(line)) {
      if (!doc?._id) continue
      if (doc.orderResolved === true) continue
      const lineStatus = String(doc.status || '').trim()
      const editType = String(doc.editType || '').trim()
      if (editType === 'invoice_reconciliation') continue
      if (
        editType === 'credit_note_request' ||
        OPEN_CREDIT_LINE_STATUSES.has(lineStatus) ||
        lineStatus.toLowerCase().includes('sent to supplier')
      ) {
        ids.push(String(doc._id))
      }
    }
  }

  return [...new Set(ids)]
}

/**
 * Open credit-ask receipting ids for a PO (bridge close after CN apply).
 */
export function listOpenCreditReceiptingIdsForPoFromHistory(
  creditNotes = [],
  purchaseOrderNumber = '',
) {
  const po = normalisePo(purchaseOrderNumber)
  if (!po) return []

  const ids = []
  for (const orderNote of creditNotes || []) {
    if (!isOpenCreditAskHistoryRow(orderNote)) continue
    const notePo = normalisePo(orderNote?._id?.purchaseOrderNumber)
    if (notePo !== po) continue
    const receipting = getReceiptingFromHistoryRow(orderNote)
    if (receipting?._id) ids.push(String(receipting._id))
  }
  return [...new Set(ids)]
}

/**
 * Credit-ask alert date: prefer receipting createdAt, else PO orderedDate.
 */
export function getCreditHistoryAlertDate(orderNote = {}) {
  const receipting = getReceiptingFromHistoryRow(orderNote)
  return (
    receipting?.createdAt ||
    orderNote?._id?.orderedDate ||
    orderNote?.orderedDate ||
    null
  )
}

/**
 * Open requests tab count — every open credit ask, i.e. the rows the tab lists.
 */
export function countOpenCreditAsksFromHistory(creditNotes = []) {
  return (creditNotes || []).filter(isOpenCreditAskHistoryRow).length
}

/**
 * Quiet line under the tabs when the amber badge (post cut-off) counts fewer
 * asks than the Open requests tab lists. Null when they agree.
 */
export function openCreditAskCutoffNote({ open = 0, newSinceCutoff = 0 } = {}) {
  if (!open || open === newSinceCutoff) return null
  const since = formatReconciliationAlertCutoff()
  return newSinceCutoff > 0
    ? `${newSinceCutoff} new since ${since}`
    : `None new since ${since}`
}

/**
 * Amber badge count — open credit asks only (post alert cut-off).
 */
export function countOpenCreditRequestsFromHistory(creditNotes = []) {
  return (creditNotes || []).filter(orderNote => {
    if (!isOpenCreditAskHistoryRow(orderNote)) return false
    const alertDate = getCreditHistoryAlertDate(orderNote)
    // Missing date: keep alertable (fail open).
    if (!alertDate) return true
    return isOnOrAfterReconciliationAlertCutoff(alertDate)
  }).length
}

export function countOpenReceiptIssuesFromHistory(creditNotes = []) {
  return (creditNotes || []).filter(isOpenReceiptIssueHistoryRow).length
}

export function filterCreditHistoryByView(
  creditNotes = [],
  view = CREDIT_REQUEST_VIEW.OPEN,
) {
  if (view === CREDIT_REQUEST_VIEW.RESOLVED) {
    return (creditNotes || []).filter(isResolvedCreditAskHistoryRow)
  }
  if (view === CREDIT_REQUEST_VIEW.RECEIPT_ISSUES) {
    return (creditNotes || []).filter(isOpenReceiptIssueHistoryRow)
  }
  return (creditNotes || []).filter(isOpenCreditAskHistoryRow)
}

/**
 * Whether a CN document is already applied on this PO (suggest resolve).
 */
export function purchaseOrderHasAppliedCreditNote(
  purchaseOrderNumber = '',
  uploadedInvoices = [],
) {
  const po = normalisePo(purchaseOrderNumber)
  if (!po) return false
  const linked = getLinkedInvoicesForReceiptingOrder(
    { purchaseOrderNumber: po },
    uploadedInvoices,
  )
  return linked.some(doc => isCreditNoteDocument(doc))
}

export function shouldShowCreditRequestHealthBadge(count) {
  return Number(count) > 0
}

export function buildCreditRequestHealthTooltip(openCount) {
  const count = Number(openCount) || 0
  if (count <= 0) return ''
  return ASK_FOR_CREDIT_BRIDGE_COPY.badgeTooltip(count)
}
